import { useEffect, useState } from 'react'
import CapstonMark from '../components/CapstonMark'
import ErrorBoundary from '../components/ErrorBoundary'
import {
  ADMIN_PATH,
  ROLE_ADMIN,
  TEMPLATES_PATH,
  adminSpacePath,
  adminSpaceRoute,
  allowedPath,
  discoveryRoute,
  homePath,
  isTemplatesPath,
  resultPath,
  resultRoute,
  spacePath,
  spaceResultPath,
} from '../lib/paths'
import DiscoveryPage from './DiscoveryPage'
import ResultPage from './ResultPage'
import SpacePage from './SpacePage'
import SpacesPage from './SpacesPage'
import TemplatesPage from './TemplatesPage'

// The signed-in app. App.jsx picks a surface; this routes WITHIN it. The
// pathname is the source of truth — it is what a refresh, a shared link and
// the receipt email all read — and the history rules are:
//
//   pushState    to open something (a result, the template editor), so Back
//                returns to where you were;
//   replaceState for a tab or attempt switch, so Back still means "leave this
//                page", not "undo every click";
//   Back         never leaves the site: an entry we pushed is unwound with
//                history.back(); a direct load has no earlier entry of ours, so
//                the fallback is pushed instead.
//
// The role rule is enforced on every path change, not only at sign-in: a
// principal who types /admin/… is shown their home, and the URL says so.
export default function AppShell({ user, onLogout, initialPath }) {
  const [path, setPath] = useState(initialPath)
  const isAdmin = user.role === ROLE_ADMIN

  useEffect(() => {
    function handlePop() {
      setPath(window.location.pathname)
    }
    window.addEventListener('popstate', handlePop)
    return () => window.removeEventListener('popstate', handlePop)
  }, [])

  // What is actually shown: the path itself when this role may see it, the
  // role's home otherwise. An unparseable /admin/… tail lands on the list.
  const shown = allowedPath(path, user.role) ? path : homePath(user.role)

  useEffect(() => {
    if (shown !== window.location.pathname) {
      window.history.replaceState(window.history.state, '', shown)
    }
  }, [shown])

  function navigate(to) {
    // The state object marks the entry as ours — back() reads it to tell an
    // entry we pushed from one the browser created on a direct load.
    window.history.pushState({ shell: true }, '', to)
    setPath(to)
  }

  function replace(to) {
    window.history.replaceState({ shell: true }, '', to)
    setPath(to)
  }

  function back(fallback) {
    if (window.history.state?.shell) {
      // We pushed this entry; unwind it so Forward still works. The popstate
      // handler does the state change.
      window.history.back()
      return
    }
    // Landed here straight from an emailed link — there is no earlier entry of
    // ours to go back to, and history.back() would leave the site entirely.
    window.history.pushState(null, '', fallback)
    setPath(fallback)
  }

  const templates = isTemplatesPath(shown)
  const adminResult = resultRoute(shown)
  const adminSpace = adminSpaceRoute(shown)
  const discovery = discoveryRoute(shown)

  let view
  if (templates) {
    view = <TemplatesPage onBack={() => back(ADMIN_PATH)} />
  } else if (adminResult) {
    // Back from a result unwinds to the tab it was opened from; a result
    // opened straight from a receipt falls back to the space's Results tab,
    // where it is listed.
    view = (
      <ResultPage
        key={adminResult.linkId}
        spaceId={adminResult.spaceId}
        linkId={adminResult.linkId}
        initialAttempt={adminResult.attempt}
        backLabel="Back to the space"
        onBack={() => back(adminSpacePath(adminResult.spaceId, 'results'))}
        onAttemptChange={(attempt) =>
          replace(resultPath(adminResult.spaceId, adminResult.linkId, attempt))
        }
      />
    )
  } else if (discovery?.linkId) {
    view = (
      <ResultPage
        key={discovery.linkId}
        spaceId={discovery.spaceId}
        linkId={discovery.linkId}
        initialAttempt={discovery.attempt}
        backLabel="Back to the space"
        onBack={() => back(spacePath(discovery.spaceId))}
        onAttemptChange={(attempt) =>
          replace(spaceResultPath(discovery.spaceId, discovery.linkId, attempt))
        }
      />
    )
  } else if (discovery) {
    // The home forwards to a lone space with replace, so Back from the space
    // leaves the site rather than bouncing back into the forward.
    view = (
      <DiscoveryPage
        route={discovery}
        user={user}
        onForward={(spaceId) => replace(spacePath(spaceId))}
        onOpen={(spaceId) => navigate(spacePath(spaceId))}
        onSpaces={isAdmin ? () => navigate(ADMIN_PATH) : null}
      />
    )
  } else if (adminSpace) {
    // Keyed on the space, not the tab: switching People | Settings keeps the
    // loaded row. Back from a result unwinds to the tab it was opened from.
    view = (
      <SpacePage
        key={adminSpace.spaceId}
        spaceId={adminSpace.spaceId}
        tab={adminSpace.tab}
        onTab={(tab) => navigate(adminSpacePath(adminSpace.spaceId, tab))}
        onSpaces={() => navigate(ADMIN_PATH)}
        onOpenResult={(linkId, attempt) =>
          navigate(resultPath(adminSpace.spaceId, linkId, attempt))
        }
      />
    )
  } else {
    // The admin home: one row per space. Remounts on every return, so it
    // reloads its counts.
    view = (
      <SpacesPage onOpenSpace={(spaceId, tab) => navigate(adminSpacePath(spaceId, tab))} />
    )
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-surface-page)' }}>
      {/* Navigation, so it prints as nothing: a filed result is the write-up. */}
      <header className="shell-header no-print">
        <div className="shell-header__left">
          {/* The brand is the way home — the role's home, so an admin who
              followed a principal's link into /discovery can get back to
              Spaces without typing a URL. */}
          <button
            type="button"
            className="shell-header__home"
            onClick={() => navigate(homePath(user.role))}
            aria-label="Home"
          >
            <CapstonMark size={30} />
            <span>
              <span className="overline">Capston</span>
              <span className="text-h5 shell-header__title">Experience</span>
            </span>
          </button>
          {/* The admin's two sections, as underlined tabs. Neither is current
              while an admin is looking at a principal's page. */}
          {isAdmin && (
            <nav className="shell-nav" aria-label="Sections">
              <button
                type="button"
                className="shell-nav__item"
                aria-current={!templates && !discovery ? 'page' : undefined}
                onClick={() => navigate(ADMIN_PATH)}
              >
                Spaces
              </button>
              <button
                type="button"
                className="shell-nav__item"
                aria-current={templates ? 'page' : undefined}
                onClick={() => navigate(TEMPLATES_PATH)}
              >
                Templates
              </button>
            </nav>
          )}
        </div>
        <div className="flex items-center" style={{ gap: 'var(--space-s)' }}>
          <span
            className="text-body-sm hidden md:inline"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {user.name}
          </span>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onLogout}>
            Log out
          </button>
        </div>
      </header>
      {/* Keyed on the path so a page that broke doesn't stay broken after
          navigating somewhere else. */}
      <ErrorBoundary key={shown}>{view}</ErrorBoundary>
    </div>
  )
}
